import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getRouteApi, useNavigate } from '@tanstack/react-router';
import { useCallback, useState } from 'react';

import { ErrorState } from '@/components/shell/ErrorState';
import { SteadoLoader } from '@/components/shell/SteadoLoader';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useAuth } from '@/lib/auth/auth-context';
import {
  exerciseRequestQueryOptions,
  resolveExerciseRequest,
} from '@/screens/exercise-requests/exercise-requests-query';
import { GymLinkPanel } from '@/screens/gym-catalog/GymLinkPanel';
import { idList, linkGymExercises } from '@/screens/gym-catalog/gym-catalog-query';

import { NewExerciseScreen } from './NewExerciseScreen';
import { RequestResolvePanel } from './RequestResolvePanel';
import { formValuesFromDraft } from './exercise-form';

const route = getRouteApi('/exercises/new');

export function NewExerciseRoute() {
  const { api } = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const search = route.useSearch();
  const gymExerciseIds = idList(search.gymExerciseIds);
  const requestId = search.exerciseRequestId ?? null;
  /** Made from gyms' exercises or a request: the row that exists now, while the second call runs. */
  const [created, setCreated] = useState<{ id: string; name: string } | null>(null);

  /** Opened from an exercise request: its draft fills the form, and the new row resolves it. */
  const request = useQuery(exerciseRequestQueryOptions(api, requestId));

  // Straight to its Media tab: the next thing to do with a new row is film
  // it, and that is the one thing the create page cannot offer.
  const openMedia = useCallback(
    (exerciseId: string) => {
      void navigate({
        to: '/exercises/$exerciseId',
        params: { exerciseId },
        search: { tab: 'media' },
        replace: true,
      });
    },
    [navigate],
  );

  /** Made from gyms' exercises ("From gyms"): linked as a second call, once the row exists. */
  const link = useMutation({
    mutationFn: (exerciseId: string) => linkGymExercises(api, gymExerciseIds, exerciseId),
    onSuccess: (_decision, exerciseId) => {
      openMedia(exerciseId);
    },
    retry: false,
  });

  /**
   * Made for an exercise request: resolved as a second call, as a food
   * request is. Folding the request into `POST /admin/exercises` would buy
   * atomicity at the cost of a create endpoint that knows about a queue; a
   * failed resolve is recoverable from here, or later with "This is it".
   */
  const resolve = useMutation({
    mutationFn: (exerciseId: string) =>
      resolveExerciseRequest(api, requestId ?? '', { exerciseId, created: true }),
    onSuccess: (_request, exerciseId) => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'exercise-requests'] });
      void queryClient.invalidateQueries({ queryKey: ['admin', 'exercise-request', requestId] });
      void queryClient.invalidateQueries({ queryKey: ['admin', 'scope-counts'] });
      openMedia(exerciseId);
    },
    retry: false,
  });

  const onCreated = useCallback(
    (exercise: { id: string; name: string }) => {
      if (gymExerciseIds.length > 0) {
        setCreated(exercise);
        link.mutate(exercise.id);
        return;
      }
      if (requestId !== null) {
        setCreated(exercise);
        resolve.mutate(exercise.id);
        return;
      }
      openMedia(exercise.id);
    },
    [openMedia, link, resolve, gymExerciseIds.length, requestId],
  );

  if (created !== null && gymExerciseIds.length > 0) {
    return (
      <GymLinkPanel
        noun="exercise"
        name={created.name}
        count={gymExerciseIds.length}
        pending={link.isPending}
        error={link.isError ? link.error.message : null}
        onRetry={() => {
          link.mutate(created.id);
        }}
        onOpen={() => {
          openMedia(created.id);
        }}
      />
    );
  }

  if (created !== null && requestId !== null) {
    return (
      <RequestResolvePanel
        name={created.name}
        requestName={request.data?.name ?? 'the exercise request'}
        pending={resolve.isPending}
        error={resolve.isError ? resolve.error.message : null}
        onRetry={() => {
          resolve.mutate(created.id);
        }}
        onOpen={() => {
          openMedia(created.id);
        }}
      />
    );
  }

  if (requestId !== null) {
    if (request.isPending) {
      return (
        <p role="status" className="flex items-center gap-2 text-sm text-muted-foreground">
          <SteadoLoader />
          Reading the exercise request…
        </p>
      );
    }
    if (request.isError) {
      return (
        <ErrorState
          title="Could not read the exercise request"
          message={request.error.message}
          onRetry={() => void request.refetch()}
        />
      );
    }
    if (request.data.status !== 'open') {
      return (
        <Alert role="alert" tone="warn">
          <AlertDescription>
            “{request.data.name}” has already been {request.data.status}, so a new exercise would answer
            nothing. Open it from Exercise requests to see what happened.
          </AlertDescription>
        </Alert>
      );
    }
    return (
      <NewExerciseScreen
        key={request.data.id}
        initialValues={formValuesFromDraft(request.data.draft?.exercise ?? null, request.data.name)}
        draftWarnings={request.data.draft?.warnings}
        onCreated={onCreated}
      />
    );
  }

  return <NewExerciseScreen initialName={search.name} onCreated={onCreated} />;
}
